import { pick } from 'es-toolkit';
import React, { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  DialogContentText,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';

import { ProductionGroup } from '@paragon/contract-library';
import { enqueueError } from '@paragon/notification-tools';
import { Dialog } from '@paragon/popup-state';
import { getProductionGroupName } from '@paragon/production-app-utilities';
import { getRanges } from '@paragon/utilities';

import {
  duplicateProductionGroup,
  loadProductionGroupData,
  removeProductionGroup,
  updateProductionGroupProperties,
} from '../../store/actions/productionGroups';
import { getScopeProjectGuids, ProjectActionScope } from '../../store/projectActionScope';
import { getBatchesByProductionGroupGuid } from '../../store/selectors/getBatchesByProductionGroupGuid';
import { getBundlesByProductionGroupGuid } from '../../store/selectors/getBundlesByProductionGroupGuid';
import { getProductionComponentInstancesByProductionGroupGuid } from '../../store/selectors/getProductionComponentInstancesByProductionGroupGuid';
import { getProductionGroupsByProjectGuid } from '../../store/selectors/getProductionGroupsByProjectGuid';
import { useAppDispatch, useAppSelector } from '../hooks';
import { productionGroupPropertiesEquals } from './CreateProductionGroupDialog';
import {
  getProductionGroupPropertiesAreValid,
  ProductionGroupProperties,
  SettableProductionGroupProperties,
} from './ProductionGroupProperties';

function getSettablePropertiesFromProductionGroup(productionGroup: ProductionGroup): SettableProductionGroupProperties {
  return pick(productionGroup, ['projectId', 'projectShortName', 'buildingType', 'buildingInstance', 'level']);
}

export function ManageProductionGroupsDialog({ scope }: { readonly scope: ProjectActionScope }) {
  const dispatch = useAppDispatch();
  const productionGroupsByProjectGuid = useAppSelector(getProductionGroupsByProjectGuid);
  const productionGroups = useAppSelector((state) => state.productionGroups);
  const batchesByProductionGroupGuid = useAppSelector(getBatchesByProductionGroupGuid);
  const bundlesByProductionGroupGuid = useAppSelector(getBundlesByProductionGroupGuid);
  const productionComponentInstancesByProductionGroupGuid = useAppSelector(
    getProductionComponentInstancesByProductionGroupGuid,
  );

  const projectGuid = getScopeProjectGuids(scope)[0];

  const productionGroupsForActiveProject = productionGroupsByProjectGuid[projectGuid];

  const [activeProductionGroupGuid, setActiveProductionGroupGuid] = useState<string | null>(
    productionGroupsForActiveProject.length > 0 ? productionGroupsForActiveProject[0].guid : null,
  );
  const [activeProductionGroupProperties, setActiveProductionGroupProperties] =
    useState<SettableProductionGroupProperties | null>(
      activeProductionGroupGuid === null
        ? null
        : getSettablePropertiesFromProductionGroup(productionGroups[activeProductionGroupGuid]),
    );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const updatingOrDuplicatingDisabled =
    !activeProductionGroupProperties ||
    !getProductionGroupPropertiesAreValid(activeProductionGroupProperties) ||
    productionGroupsForActiveProject.some((productionGroupForActiveProject) =>
      productionGroupPropertiesEquals(productionGroupForActiveProject, activeProductionGroupProperties),
    );

  const batchCountForActiveProductionGroup = activeProductionGroupGuid
    ? batchesByProductionGroupGuid[activeProductionGroupGuid].length
    : 0;
  const bundleCountForActiveProductionGroup = activeProductionGroupGuid
    ? bundlesByProductionGroupGuid[activeProductionGroupGuid].length
    : 0;
  const trussCountForActiveProductionGroup = activeProductionGroupGuid
    ? productionComponentInstancesByProductionGroupGuid[activeProductionGroupGuid].length
    : 0;
  const productionGroupContents = `${batchCountForActiveProductionGroup} batch${
    batchCountForActiveProductionGroup === 1 ? '' : 'es'
  }, ${bundleCountForActiveProductionGroup} bundle${
    bundleCountForActiveProductionGroup === 1 ? '' : 's'
  }, ${trussCountForActiveProductionGroup} truss${trussCountForActiveProductionGroup === 1 ? '' : 'es'}`;

  return (
    <Dialog
      title="Manage Production Groups"
      open
      submitText="Close"
      cancelDisabled
      contentSx={{ width: '800px', height: '450px' }}
    >
      <Grid container spacing={1}>
        <Grid size={6}>
          <List>
            {productionGroupsForActiveProject.map((productionGroup) => (
              <ListItemButton
                key={productionGroup.guid}
                selected={productionGroup.guid === activeProductionGroupGuid}
                onClick={async () => {
                  setActiveProductionGroupGuid(productionGroup.guid);
                  setActiveProductionGroupProperties(getSettablePropertiesFromProductionGroup(productionGroup));

                  setDataLoading(true);
                  await dispatch(loadProductionGroupData(productionGroup.guid));
                  setDataLoading(false);
                }}
              >
                <ListItemText primary={getProductionGroupName(productionGroup, true)} />
              </ListItemButton>
            ))}
          </List>
        </Grid>
        <Grid size={6}>
          {activeProductionGroupGuid === null || activeProductionGroupProperties === null ? (
            'Select a production group to edit, duplicate, or delete'
          ) : (
            <>
              <Typography>{productionGroupContents}</Typography>
              <ProductionGroupProperties
                productionGroupProperties={activeProductionGroupProperties}
                submit={setActiveProductionGroupProperties}
              />
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Tooltip
                  disableInteractive
                  title={
                    updatingOrDuplicatingDisabled
                      ? 'Set unique properties before duplicating'
                      : 'Duplicate the selected Production Group with the changed properties'
                  }
                >
                  <span>
                    <Button
                      onClick={async () => {
                        const newProductionGroup = await dispatch(
                          duplicateProductionGroup({
                            ...activeProductionGroupProperties,
                            guid: activeProductionGroupGuid,
                          }),
                        );
                        if (newProductionGroup !== null) setActiveProductionGroupGuid(newProductionGroup.guid);
                      }}
                      startIcon={<ContentCopyIcon />}
                      variant="outlined"
                      disabled={updatingOrDuplicatingDisabled}
                    >
                      Duplicate
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  disableInteractive
                  title={
                    updatingOrDuplicatingDisabled
                      ? 'Set unique properties before updating'
                      : 'Update the selected Production Group'
                  }
                >
                  <span>
                    <Button
                      onClick={() =>
                        void dispatch(
                          updateProductionGroupProperties(activeProductionGroupProperties, activeProductionGroupGuid),
                        )
                      }
                      sx={{ ml: 1 }}
                      startIcon={<SaveIcon />}
                      variant="outlined"
                      disabled={updatingOrDuplicatingDisabled}
                    >
                      Update
                    </Button>
                  </span>
                </Tooltip>

                <Button
                  onClick={() => {
                    const scheduledBundlesForActiveProductionGroup = bundlesByProductionGroupGuid[
                      activeProductionGroupGuid
                    ].filter((bundle) => bundle.scheduleGroupGuid !== null);
                    if (scheduledBundlesForActiveProductionGroup.length > 0) {
                      dispatch(
                        enqueueError(
                          `Cannot delete a production group that has scheduled bundles (${getRanges(
                            scheduledBundlesForActiveProductionGroup.map((bundle) => bundle.id),
                          )})`,
                        ),
                      );
                      return;
                    }
                    setConfirmDeleteOpen(true);
                  }}
                  sx={{ ml: 1 }}
                  startIcon={<DeleteIcon />}
                  variant="outlined"
                  disabled={dataLoading}
                >
                  Delete
                </Button>
                <Dialog
                  open={confirmDeleteOpen}
                  variant="design"
                  title="Confirm Delete"
                  onSubmit={() => {
                    dispatch(removeProductionGroup(activeProductionGroupGuid));
                    setActiveProductionGroupGuid(null);
                    setActiveProductionGroupProperties(null);
                    setConfirmDeleteOpen(false);
                  }}
                  onCancel={() => void setConfirmDeleteOpen(false)}
                >
                  <DialogContentText>
                    Delete production group {getProductionGroupName(productionGroups[activeProductionGroupGuid], true)}{' '}
                    including its {productionGroupContents}? The designs will not be lost; they will remain in the
                    project.
                  </DialogContentText>
                </Dialog>
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
}
